import { useState, useEffect, useRef, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faSortUp,
  faSortDown,
  faSort,
} from "@fortawesome/free-solid-svg-icons";
import { API_URL } from "../../config";
import axios from "axios";
import { Link } from "react-router-dom";
import LoadingSpinner from "../../components/LoadingSpinner";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";
import '../css/UserManagement.css'

const ESIManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;
  const token = localStorage.getItem("authToken");

  // Sorting state
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("ASC"); // ASC | DESC

  // Define allowed columns for sorting
  const allowedOrderColumns = [
    'user_fullname',
    'user_email',
    'user_code',
    'user_designation',
    'user_category',
    'esinumber',
    'uan_number',
    'employmentstatus',
  ];

  // Toggle sorting for a column
  const toggleSort = (column) => {
    if (!allowedOrderColumns.includes(column)) return;
    if (sortField === column) {
      setSortDirection((prev) => (prev === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortField(column);
      setSortDirection("ASC");
    }
  };

  // Get the appropriate sort icon for a column
  const getSortIcon = (column) => {
    if (sortField === column) {
      return sortDirection === "ASC" ? faSortUp : faSortDown;
    }
    return faSort;
  };

  // 🔎 Search Filters
  const [nameFilter, setNameFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [userCodeFilter, setUserCodeFilter] = useState("");
  const [designationFilter, setDesignationFilter] = useState("");
  const [esiFilter, setEsiFilter] = useState("");
  const [uanFilter, setUanFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const fetchESIManagement = async () => {
    try {
      setLoading(true);

      // Build query parameters
      const params = {
        limit,
        page,
      };

      // Add sorting parameters if specified
      if (sortField && allowedOrderColumns.includes(sortField)) {
        params.orderBy = sortField;
        params.orderDir = sortDirection;
      }

      const response = await axios.get(`${API_URL}EsiMangement`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        params,
      });

      setUsers(response.data.data.users);
      setPageCount(response.data.data.totalPages);
      setTotal(response.data.data.total);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchESIManagement();
  }, [page, sortField, sortDirection]); // Re-fetch when page or sorting changes

  // 🔎 Apply filters
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      if (
        nameFilter &&
        !(user.user_fullname || "")
          .toLowerCase()
          .includes(nameFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        emailFilter &&
        !(user.user_email || "")
          .toLowerCase()
          .includes(emailFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        userCodeFilter &&
        !(user.user_code || "")
          .toLowerCase()
          .includes(userCodeFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        designationFilter &&
        !(user.user_designation || "")
          .toLowerCase()
          .includes(designationFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        esiFilter &&
        !(user.esinumber || "").toLowerCase().includes(esiFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        uanFilter &&
        !(user.uan_number || "").toLowerCase().includes(uanFilter.toLowerCase())
      ) {
        return false;
      }
      if (statusFilter) {
        const statusText = user.user_blocked == 1 ? "activated" : "deactivated";
        if (!statusText.includes(statusFilter.toLowerCase())) {
          return false;
        }
      }
      return true;
    });
  }, [
    users,
    nameFilter,
    emailFilter,
    userCodeFilter,
    designationFilter,
    esiFilter,
    uanFilter,
    statusFilter,
  ]);

  useEffect(() => {
    setFilterLoading(true);

    const timer = setTimeout(() => {
      setFilterLoading(false);
    }, 300); // smooth UX

    return () => clearTimeout(timer);
  }, [
    nameFilter,
    emailFilter,
    userCodeFilter,
    designationFilter,
    esiFilter,
    uanFilter,
    statusFilter,
  ]);


  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  useEffect(() => {
    const handleClearFilters = () => {
      setNameFilter("");
      setEmailFilter("");
      setUserCodeFilter("");
      setDesignationFilter("");
      setEsiFilter("");
      setUanFilter("");
      setStatusFilter("");
    };

    window.addEventListener('clearEsiManagementFiltersEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearEsiManagementFiltersEvent', handleClearFilters);
    };
  }, []);

  return (
    <div className="page-header-fixed page-sidebar-fixed mt-5">
      {/* {loading && <LoadingSpinner />} */}
      <div class="panel panel-default with-rounded-corner with-shadow">
        <div className="d-flex align-items-center justify-content-between">
          <div className="panel-heading border-0">
            <h4 class="panel-title">Manage User ESI</h4>
          </div>
          <div className="me-3 ">
            <span style={{ fontSize: "0.8rem" }} className="me-2">
              {start} to {end} of {total}
            </span>
            <button
              className="btn btn-default btn-sm"
              title="Previous"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            &nbsp;
            <button
              className="btn btn-default btn-sm"
              title="Next"
              onClick={() => handlePageChange(page + 1)}
              disabled={page === pageCount}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>

        <div className="table-responsive user-table-container">
          <table className="table m-b-0">
            <thead>
              <tr>
                <th className="text-center">#</th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleSort("user_fullname")}
                >
                  Name <FontAwesomeIcon icon={getSortIcon("user_fullname")} />
                </th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleSort("user_email")}
                >
                  Email Id <FontAwesomeIcon icon={getSortIcon("user_email")} />
                </th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleSort("user_code")}
                >
                  User Code <FontAwesomeIcon icon={getSortIcon("user_code")} />
                </th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleSort("user_designation")}
                >
                  Designation <FontAwesomeIcon icon={getSortIcon("user_designation")} />
                </th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleSort("esinumber")}
                >
                  ESI Number <FontAwesomeIcon icon={getSortIcon("esinumber")} />
                </th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleSort("uan_number")}
                >
                  UAN Number <FontAwesomeIcon icon={getSortIcon("uan_number")} />
                </th>
                <th>Status</th>
                <th>Action</th>
              </tr>
              {/* 🔎 Search Filters Row */}
              <tr>
                <th></th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Search name..."
                    value={nameFilter}
                    onChange={(e) => setNameFilter(e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Search email..."
                    value={emailFilter}
                    onChange={(e) => setEmailFilter(e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Search code..."
                    value={userCodeFilter}
                    onChange={(e) => setUserCodeFilter(e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Search designation..."
                    value={designationFilter}
                    onChange={(e) => setDesignationFilter(e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Search ESI..."
                    value={esiFilter}
                    onChange={(e) => setEsiFilter(e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Search UAN..."
                    value={uanFilter}
                    onChange={(e) => setUanFilter(e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="form-control input-sm"
                    placeholder="Search status..."
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  />
                </th>
                <th>
                  <button
                    className="btn btn-default btn-sm"
                    onClick={() => {
                      setNameFilter("");
                      setEmailFilter("");
                      setUserCodeFilter("");
                      setDesignationFilter("");
                      setEsiFilter("");
                      setUanFilter("");
                      setStatusFilter("");
                    }}
                  >
                    Clear
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading || filterLoading ? (
                Array.from({ length: 8 }).map((_, index) => (
                  <tr key={index} className="skeleton-row">
                    <td className="text-center"><div className="skeleton-box" /></td>
                    <td><div className="skeleton-box" /></td>
                    <td><div className="skeleton-box" /></td>
                    <td><div className="skeleton-box" /></td>
                    <td><div className="skeleton-box" /></td>
                    <td><div className="skeleton-box" /></td>
                    <td><div className="skeleton-box" /></td>
                    <td><div className="skeleton-box" style={{ width: "80px" }} /></td>
                    <td><div className="skeleton-box" style={{ width: "60px" }} /></td>
                  </tr>
                ))
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map((user, index) => {
                  return (
                    <tr key={user.id}>
                      <td className="text-center">
                        {(page - 1) * limit + index + 1}
                      </td>
                      <td>{user.user_fullname}</td>
                      <td>{user.user_email}</td>
                      <td>{user.user_code}</td>
                      <td>{user.user_designation}</td>
                      <td>{user.esinumber}</td>
                      <td>{user.uan_number}</td>
                      <td>
                        {user.user_blocked == 1 ? (
                          <label className="btn btn-primary btn-xs m-b-3">
                            Activated
                          </label>
                        ) : (
                          <label className="btn btn-danger btn-xs m-b-3">
                            Deactivated
                          </label>
                        )}
                      </td>
                      <td>
                        <PermissionWrapper module="hrms_esimgmt" action="edit">
                          <Link
                            className="btn-warning btn-xs"
                            style={{ float: "left", marginRight: "10px" }}
                            to={`/Hrms/EsiManagement/edit/${user.id}`}
                            title="Edit User"
                          >
                            <i className="ti ti-pencil"></i>
                          </Link>
                        </PermissionWrapper>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" style={{ textAlign: "center" }}>
                    No Records Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ESIManagement;